import styled from 'styled-components';
import { useState, useEffect, useRef } from 'react';
import { useRouter } from 'next/router';
import Link from 'next/link';
// components
import NotificationCenter from 'components/organisms/common/NotificationCenter';
import Logo from 'components/molecules/headers/Logo';
import SearchInput from 'components/molecules/headers/SearchInput';
import Avatar from 'components/atoms/photos/Avatar';
import NotifyIcon from 'components/atoms/icons/Notify';
import IconButton from 'cds/buttons/Icon';
import Icon from 'cds/icons';
// hooks
import { useSelector } from 'hooks/common/useStore';
import useCommonBridge from 'hooks/bridge/useCommonBridge';
import useBridgeCallback from 'hooks/bridge/useBridgeCallback';
// utils
import deviceChecker from 'lib/utils/deviceChecker';
import { isExistWindow } from 'lib/utils/helper';
// styles
import { palette, zIndex, mediaQuery } from 'cds/styles';

/** 클래스 헤더 */
const ClassHeader = ({ ...props }) => {
  const router = useRouter();
  const [userInfo, existNotification] = useSelector(state => [
    state.user.info,
    state.user.newsCount.news_unread_count,
  ]);

  const [isEnableNotify, setIsEnableNotify] = useState(false);
  const [isVisibleNotify, setIsVisibleNotify] = useState(false);
  const header = useRef(null);

  const { onCloseWebView, goToProfile } = useCommonBridge();

  const isExistUser = userInfo.isLoaded && userInfo.id;

  useBridgeCallback({ onOpenNotify });

  const onBackButton = () => {
    if (!onCloseWebView()) {
      router.back();
    }
  };

  const onLogin = () => {
    const loginRedirectUrl = isExistWindow() ? window.location.pathname : '';
    router.push(`/user?url=${loginRedirectUrl}`);
  };

  const onToggleNotify = () => setIsVisibleNotify(v => !v);

  function onOpenNotify() {
    const notifyEvent = new CustomEvent('onOpenNotify', {
      detail: {
        winWidth: window.outerWidth,
      },
    });

    document.dispatchEvent(notifyEvent);
  }

  const doOpenNotify = ({ detail }) => {
    const { winWidth } = detail;
    if (winWidth < mediaQuery.large) {
      return;
    }

    setIsVisibleNotify(true);
  };

  const onProfile = () => goToProfile(userInfo.id);

  /** 알림 아이콘 표시 여부 */
  useEffect(() => {
    const device = deviceChecker();
    if (device.app()) {
      setIsEnableNotify(false);
    } else {
      setIsEnableNotify(true);
    }
  }, []);

  useEffect(() => {
    if (!isEnableNotify) {
      return;
    }

    document.addEventListener('onOpenNotify', doOpenNotify);

    return () => {
      document.removeEventListener('onOpenNotify', doOpenNotify);
    };
  }, [isEnableNotify]);

  const LeftSide = (
    <LeftSideLayout>
      <BackButton width={24} height={54} mode="Dark" onClick={onBackButton}>
        <Icon name="ic_back_m" width={24} height={24} />
      </BackButton>
      <Logo />
    </LeftSideLayout>
  );

  const RightSide = (
    <RightSideLayout>
      <Link href={`${process.env.NEXT_PUBLIC_DEFAULT_HOST}/class/open`} passHref>
        <ClassOpenButton>클래스개설</ClassOpenButton>
      </Link>

      {isExistUser && isEnableNotify && (
        <>
          <IconButton width={30} height={30} mode="Dark" onClick={onToggleNotify}>
            <NotifyIcon width={30} height={30} isActive={!!existNotification} />
          </IconButton>
        </>
      )}

      {isExistUser && (
        <AvatarButton onClick={onProfile}>
          <Avatar width={32} height={32} src={userInfo.photo} alt={userInfo.name} />
        </AvatarButton>
      )}

      {!isExistUser && userInfo.isLoaded && <LoginButton onClick={onLogin}>로그인</LoginButton>}

      {!userInfo.isLoaded && <LoginSkeleton />}
    </RightSideLayout>
  );

  return (
    <>
      <Layout ref={header} {...props}>
        <ContentLayout>
          <Item>{LeftSide}</Item>

          <SearchInput mode="Dark" />

          <Item>{RightSide}</Item>
        </ContentLayout>
      </Layout>

      {isExistUser && <NotificationCenter isVisible={isVisibleNotify} onClose={onToggleNotify} />}
    </>
  );
};

// TODO PHP와 임시적으로 색상 통일되어있음
const Layout = styled.header`
  width: 100%;
  height: 56px;
  border-bottom: ${props => (props.noBorderBottom ? 'none' : `1px solid ${palette.border.b1}`)};
  z-index: ${zIndex.header};
  background-color: #131313;
  transform: translateZ(0);

  ${mediaQuery.large} {
    height: 84px;
  }
`;

const ContentLayout = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  white-space: nowrap;
  flex-wrap: nowrap;
  width: 1400px;
  max-width: 100%;
  height: 100%;
  padding: 0 16px;
  margin: auto;
`;

const LoginSkeleton = styled.div`
  width: 76px;
  height: 56px;

  ${mediaQuery.large} {
    height: 84px;
  }
`;

const Item = styled.div`
  flex-grow: 1;
  width: 50px;

  &:first-child {
    display: flex;
    justify-content: flex-start;
  }

  &:last-child {
    display: flex;
    justify-content: flex-end;
  }
`;

const LeftSideLayout = styled.div`
  flex-grow: 1;
  display: flex;
  align-items: center;
  justify-content: flex-start;
`;

const RightSideLayout = styled.div`
  flex-grow: 1;
  display: none;
  position: relative;

  ${mediaQuery.large} {
    display: flex;
    align-items: center;
    justify-content: flex-end;
  }
`;

const BackButton = styled(IconButton)`
  margin-right: 12px;
`;

// TODO PHP와 임시적으로 색상 통일되어있음
const ClassOpenButton = styled.a`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 32px;
  padding: 4px 12px;
  margin-right: 14px;
  border-radius: 4px;
  background: #2b2b2b;
  font-weight: 500;
  font-size: 14px;
  color: ${palette.white};
`;

const AvatarButton = styled.button`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  margin-left: 14px;
`;

const LoginButton = styled.a`
  height: 32px;
  padding: 0 4px;
  border: 1px solid ${palette.border.b1};
  border-radius: 4px;
  font-size: 12px;
  line-height: 30px;
  color: ${palette.grey010};
  cursor: pointer;
`;

export default ClassHeader;
